import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, X } from "lucide-react";
import { api, MONTHS } from "../lib/api";

const months = MONTHS.map((m) => m.value);

function currentPeriod() {
  const now = new Date();
  return { tahun: String(now.getFullYear()), bulan: months[now.getMonth()] };
}

/**
 * Fast RT search — matches by "rw", "rt", "rw/rt", "rw rt", or free-text like "01 003".
 * Returns list of {rw, rt} from the region master (all 75 RTs).
 */
export default function RtSearch({ onNavigate }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [rtIndex, setRtIndex] = useState([]); // [{rw, rt}]
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.regions();
        if (!alive) return;
        const flat = [];
        (r.rw_list || []).forEach((row) => {
          for (let i = 1; i <= row.jumlah_rt; i++) {
            flat.push({ rw: row.rw, rt: String(i).padStart(3, "0") });
          }
        });
        setRtIndex(flat);
      } catch (err) {
        console.warn("RtSearch: gagal memuat daftar RT", err);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const results = useMemo(() => {
    if (!q.trim()) return rtIndex.slice(0, 8);
    // Extract 1-3 number tokens from user input
    const digits = q.replace(/[^\d]/g, " ").trim().split(/\s+/).filter(Boolean);
    let rwFilter = null, rtFilter = null;
    if (digits.length === 1) {
      // Could be RW (1-2 digits) or RT (3 digits)
      if (digits[0].length >= 3) rtFilter = digits[0].padStart(3, "0");
      else rwFilter = digits[0].padStart(2, "0");
    } else if (digits.length >= 2) {
      rwFilter = digits[0].padStart(2, "0");
      rtFilter = digits[1].padStart(3, "0");
    }
    return rtIndex.filter((r) => {
      if (rwFilter && r.rw !== rwFilter) return false;
      if (rtFilter && !r.rt.includes(rtFilter.replace(/^0+/, ""))) {
        // relaxed: allow partial rt match
        if (r.rt !== rtFilter) return false;
      }
      return true;
    }).slice(0, 12);
  }, [q, rtIndex]);

  useEffect(() => { setActiveIdx(0); }, [q]);

  const go = (r) => {
    if (!r) return;
    const p = currentPeriod();
    setOpen(false);
    setQ("");
    onNavigate && onNavigate();
    nav(`/detail?rw=${r.rw}&rt=${r.rt}&tahun=${p.tahun}&bulan=${p.bulan}`);
  };

  const onKey = (e) => {
    if (!open) setOpen(true);
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative mx-3 mb-3" ref={wrapRef}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--sidebar-text-muted)]" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Cari RT · mis. 04 007"
          className="w-full h-9 pl-8 pr-7 rounded-lg bg-white/[0.06] border border-white/10 text-white placeholder:text-[color:var(--sidebar-text-muted)] text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#34d399]/40 focus:border-transparent"
          data-testid="rt-search-input"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--sidebar-text-muted)] hover:text-white">
            <X size={13} />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 mt-1 z-40 bg-white rounded-lg shadow-xl border border-[hsl(var(--border))] overflow-hidden max-h-72 overflow-y-auto" data-testid="rt-search-results">
          {results.map((r, i) => (
            <button
              key={`${r.rw}-${r.rt}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => go(r)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs transition ${i === activeIdx ? "bg-[hsl(var(--muted))] text-[#0e2b26]" : "text-[#0e2b26] hover:bg-[hsl(var(--muted))]/60"}`}
              data-testid={`rt-result-${r.rw}-${r.rt}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-11 h-6 rounded-md bg-[#0e2b26] text-white font-bold text-[0.65rem] tracking-wider flex items-center justify-center">RW {r.rw}</span>
                <span className="font-semibold">RT {r.rt}</span>
              </div>
              <ChevronRight size={13} className="text-[hsl(var(--muted-foreground))]" />
            </button>
          ))}
        </div>
      )}
      {open && q && results.length === 0 && (
        <div className="absolute left-0 right-0 mt-1 z-40 bg-white rounded-lg shadow-xl border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
          Tidak ada RT yang cocok.
        </div>
      )}
    </div>
  );
}
